import React, { memo, useCallback } from "react";
import { MachinesListWithCompanies } from "../machines-list-with-companies/machines-list-with-companies";
import { MachinesListCompanyEntry } from "@src/types/gateways-with-machines";
import styles from "./machines-list-companies-wrapper.modules.scss";
import { MachinesListHeaderExpandCollapseAll } from "@src/components/molecules/machines-list-header-expand-collapse-all/machines-list-header-expand-collapse-all";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  setAllMachinesWithGatewaysListData,
  setSearchMachinesWithGatewaysListData,
} from "@src/reducers/gateways-machines-slice";
import { RootState } from "@src/reducers/store";

const cssClassName = "machines-list-companies-wrapper";

interface MachinesListCompaniesWrapperPropsTypes {
  showSearchMachinesOrAllMachinesListData?: Record<
    string,
    MachinesListCompanyEntry
  >;
  selectedMachineId?: string;
}

export const MachinesListCompaniesWrapper = memo(
  ({
    showSearchMachinesOrAllMachinesListData,
    selectedMachineId,
  }: MachinesListCompaniesWrapperPropsTypes) => {
    const dispatch = useDispatch();

    const machinesManagerSearchTerms = useSelector(
      (s: RootState) => s.gatewaysMachinesSlice.machinesManagerSearchTerms,
      shallowEqual,
    );

    const hasActiveSearch = machinesManagerSearchTerms.length > 0;
    const companyKeys = Object.keys(
      showSearchMachinesOrAllMachinesListData ?? {},
    );

    const handleCompanyToggle = useCallback(
      (companyKey: string) => {
        if (!showSearchMachinesOrAllMachinesListData?.[companyKey]) return;

        const currentVisible =
          showSearchMachinesOrAllMachinesListData[companyKey]
            ?.sources_visible ?? true;

        dispatch(
          (hasActiveSearch
            ? setSearchMachinesWithGatewaysListData
            : setAllMachinesWithGatewaysListData)({
            ...showSearchMachinesOrAllMachinesListData,
            [companyKey]: {
              ...showSearchMachinesOrAllMachinesListData[companyKey],
              sources_visible: !currentVisible,
            },
          }),
        );
      },
      [showSearchMachinesOrAllMachinesListData, hasActiveSearch, dispatch],
    );

    const handleSetAll = useCallback(
      (nextVisible: boolean) => {
        if (
          !showSearchMachinesOrAllMachinesListData ||
          companyKeys.length === 0
        )
          return;

        const updatedState: Record<string, MachinesListCompanyEntry> = {};

        companyKeys.forEach((key) => {
          updatedState[key] = {
            ...showSearchMachinesOrAllMachinesListData[key],
            sources_visible: nextVisible,
          };
        });

        dispatch(
          (hasActiveSearch
            ? setSearchMachinesWithGatewaysListData
            : setAllMachinesWithGatewaysListData)(updatedState),
        );
      },
      [
        showSearchMachinesOrAllMachinesListData,
        companyKeys,
        hasActiveSearch,
        dispatch,
      ],
    );

    const handleExpandAll = useCallback(
      () => handleSetAll(true),
      [handleSetAll],
    );

    const handleCloseAll = useCallback(
      () => handleSetAll(false),
      [handleSetAll],
    );

    if (companyKeys.length === 0) {
      return null;
    }

    const companyValues = companyKeys
      .map((key) => showSearchMachinesOrAllMachinesListData?.[key])
      .filter(
        (companyData): companyData is MachinesListCompanyEntry =>
          companyData !== undefined,
      );

    return (
      <div className={styles[cssClassName]}>
        <MachinesListHeaderExpandCollapseAll
          companyEntries={companyValues}
          onExpandAll={handleExpandAll}
          onCloseAll={handleCloseAll}
        />
        <div className={styles[`${cssClassName}__content`]}>
          {companyValues.map((companyData) => (
            <MachinesListWithCompanies
              key={companyData?.name}
              companyData={companyData}
              isCompanyExpanded={companyData?.sources_visible ?? true}
              handleCompanyToggle={() => handleCompanyToggle(companyData.name)}
              selectedMachineId={selectedMachineId}
            />
          ))}
        </div>
      </div>
    );
  },
);

MachinesListCompaniesWrapper.displayName = "MachinesListCompaniesWrapper";
